/**
 * Wishlist System
 * ----------------------------------------------------------------------------
 * Handles:
 *   - Wishlist storage (localStorage for guests, customer metafield for logged-in)
 *   - Wishlist drawer open/close
 *   - Wishlist button toggling on product cards & product page
 *   - Hydrating drawer items from product handles via /products/:handle.js
 *   - Wishlist page rendering (if `data-wishlist-page` element exists)
 *
 * Storage shape (always an array of product handles):
 *   ["product-handle-1", "product-handle-2", ...]
 *
 * Customer metafield namespace/key:
 *   namespace: "wishlist", key: "items"  (JSON stringified array of handles)
 *   Sync requires a small App Proxy or theme-app-extension in production.
 *   This file uses Shopify's customer-only API where possible, and falls back
 *   to localStorage if metafield writes are not available.
 *
 * To enable cross-device sync via customer metafields, add an app proxy that
 * accepts POST { handles: [...] } at /apps/wishlist (see install notes in
 * sections/wishlist.liquid). Until then, logged-in users still get
 * localStorage persistence — which works perfectly on a single browser.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'ffk_wishlist_handles';
  const CHANGE_EVENT = 'wishlist:change';

  // ------------------------------------------------------------------
  // Storage layer
  // ------------------------------------------------------------------
  const Storage = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    write(handles) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        /* localStorage unavailable */
      }
      // Optional cross-device sync hook. Themes can attach an app proxy at
      // /apps/wishlist/sync that accepts POST { handles: [...] }. We trigger
      // this only when the visitor is signed in AND the theme indicates the
      // proxy is actually installed via [data-wishlist-sync-url].
      if (
        document.querySelector('[data-wishlist-customer]') &&
        document.querySelector('[data-wishlist-sync-url]')
      ) {
        try {
          fetch('/apps/wishlist/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ handles: handles }),
            credentials: 'same-origin',
          }).catch(() => {});
        } catch (e) {
          /* network unavailable */
        }
      }
    },

    clear() {
      this.write([]);
    },
  };

  // ------------------------------------------------------------------
  // Wishlist controller (singleton)
  // ------------------------------------------------------------------
  const Wishlist = {
    handles: [],
    sharedHandles: null, // populated from ?items=... when viewing someone else's wishlist

    init() {
      // Parse a shared wishlist link: /pages/wishlist?items=handle-1,handle-2
      try {
        const params = new URLSearchParams(window.location.search);
        const itemsParam = params.get('items');
        if (itemsParam) {
          const parsed = itemsParam
            .split(',')
            .map((h) => h.trim())
            .filter(Boolean);
          if (parsed.length > 0) this.sharedHandles = parsed;
        }
      } catch (e) {
        /* ignore bad URL */
      }

      // Seed from server-rendered customer metafield if present
      const seedEl = document.querySelector('[data-wishlist-seed]');
      let seedHandles = null;
      if (seedEl) {
        try {
          const raw = seedEl.getAttribute('data-wishlist-seed');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) seedHandles = parsed;
          }
        } catch (e) {
          /* ignore bad seed */
        }
      }

      const local = Storage.read();
      if (seedHandles && seedHandles.length > 0 && local.length === 0) {
        // Customer logged in with cloud-saved wishlist, nothing in this browser yet
        this.handles = seedHandles.slice();
        Storage.write(this.handles);
      } else {
        this.handles = local;
      }
      this.broadcast();
    },

    has(handle) {
      return this.handles.indexOf(handle) !== -1;
    },

    add(handle) {
      if (!handle || this.has(handle)) return;
      this.handles.push(handle);
      Storage.write(this.handles);
      this.broadcast();
    },

    remove(handle) {
      const idx = this.handles.indexOf(handle);
      if (idx === -1) return;
      this.handles.splice(idx, 1);
      Storage.write(this.handles);
      this.broadcast();
    },

    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },

    clear() {
      this.handles = [];
      Storage.clear();
      this.broadcast();
    },

    broadcast() {
      document.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, { detail: { handles: this.handles.slice() } })
      );
    },
  };

  // Expose for other scripts / debugging
  window.Wishlist = Wishlist;

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Shared escape helpers — used by both WishlistDrawer.renderItem()
  // and WishlistPage.renderCard(). Defined here at the module level
  // so neither class can drift away from the other (regression fix
  // for F-001: the card was missing escaping the drawer already had).
  // ------------------------------------------------------------------
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  function sizedImage(url, size) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(size));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  // ------------------------------------------------------------------
  // Wishlist toggle buttons (cards + product page)
  // ------------------------------------------------------------------
  function syncButton(btn) {
    const handle = btn.getAttribute('data-product-handle');
    const isOn = Wishlist.has(handle);
    btn.classList.toggle('is-active', isOn);
    btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    btn.setAttribute('aria-label', isOn ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    Wishlist.toggle(handle);
    syncButton(btn);
  });

  document.addEventListener(CHANGE_EVENT, syncAllButtons);

  // ------------------------------------------------------------------
  // Wishlist drawer custom element
  // ------------------------------------------------------------------
  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this._cache = {};
      this._loaded = false;
    }

    connectedCallback() {
      const overlay = this.querySelector('#WishlistDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      // Close-button handler. Replaces former inline onclick handler in
      // snippets/wishlist-drawer.liquid. Bound in connectedCallback because
      // the close button lives in the drawer header (not the body that
      // gets re-rendered).
      const closeButtons = this.querySelectorAll('[data-close-wishlist]');
      closeButtons.forEach((btn) => btn.addEventListener('click', this.close.bind(this)));

      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });

      // Header icon trigger
      const triggers = document.querySelectorAll('[data-wishlist-trigger]');
      triggers.forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });

      // Re-render whenever the wishlist changes (so an item removed from
      // a product page is reflected the next time the drawer opens)
      document.addEventListener(CHANGE_EVENT, () => {
        if (this.classList.contains('active')) this.render();
      });
    }

    open(triggeredBy) {
      this._lastTrigger = triggeredBy || null;
      setTimeout(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      this.render();
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        this._lastTrigger.focus();
      }
    }

    async render() {
      const body = this.querySelector('#WishlistDrawer-Body');
      const list = this.querySelector('[data-wishlist-list]');
      const empty = this.querySelector('[data-wishlist-empty]');
      const footer = this.querySelector('[data-wishlist-footer]');
      if (!list || !empty) return;

      const handles = Wishlist.handles.slice();
      if (handles.length === 0) {
        list.hidden = true;
        list.innerHTML = '';
        empty.hidden = false;
        if (footer) footer.hidden = true;
        return;
      }

      empty.hidden = true;
      list.hidden = false;
      if (footer) footer.hidden = false;

      list.innerHTML = '<li class="wishlist-drawer__loading">Loading…</li>';

      const products = await this.batchFetch(handles);
      const valid = products.filter(Boolean);

      // Prune any handles whose products no longer exist
      const validHandles = valid.map((p) => p.handle);
      handles.forEach((h) => {
        if (validHandles.indexOf(h) === -1) Wishlist.remove(h);
      });

      if (valid.length === 0) {
        list.hidden = true;
        list.innerHTML = '';
        empty.hidden = false;
        if (footer) footer.hidden = true;
        return;
      }

      list.innerHTML = valid.map((p) => this.renderItem(p)).join('');
      this.bindItemActions(list);
    }

    async batchFetch(handles) {
      const results = [];
      const batch = 6;
      for (let i = 0; i < handles.length; i += batch) {
        results.push(...await Promise.all(
          handles.slice(i, i + batch).map((h) => this.fetchProduct(h))
        ));
      }
      return results;
    }

    async fetchProduct(handle) {
      if (this._cache[handle]) return this._cache[handle];
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) return null;
        const data = await res.json();
        this._cache[handle] = data;
        return data;
      } catch (e) {
        return null;
      }
    }

    renderItem(product) {
      const variant = product.variants && product.variants[0];
      const variantId = variant ? variant.id : '';
      const available = variant ? variant.available : false;
      const image = sizedImage(product.featured_image, 200);
      const price = this.formatMoney(product.price);

      const safeTitle = this.escapeHtml(product.title);
      const safeUrl = this.escapeAttr(product.url);
      const safeImage = this.escapeAttr(image);
      const safeHandle = this.escapeAttr(product.handle);

      return [
        '<li class="wishlist-drawer__item" data-wishlist-item-handle="' + safeHandle + '">',
        '  <a href="' + safeUrl + '" class="wishlist-drawer__item-image">',
        image ? '    <img src="' + safeImage + '" alt="' + safeTitle + '" loading="lazy">' : '',
        '  </a>',
        '  <div class="wishlist-drawer__item-info">',
        '    <a href="' + safeUrl + '" class="wishlist-drawer__item-title">' + safeTitle + '</a>',
        '    <span class="wishlist-drawer__item-price">' + price + '</span>',
        '  </div>',
        '  <div class="wishlist-drawer__item-actions">',
        '    <button type="button" class="wishlist-drawer__item-add" data-action="add-to-cart" data-variant-id="' +
          variantId +
          '"' +
          (available ? '' : ' disabled') +
          '>' +
          (available ? 'Add to cart' : 'Sold out') +
          '</button>',
        '    <button type="button" class="wishlist-drawer__item-remove" data-action="remove">Remove</button>',
        '  </div>',
        '</li>',
      ].join('\n');
    }

    bindItemActions(list) {
      list.querySelectorAll('.wishlist-drawer__item').forEach((item) => {
        const handle = item.getAttribute('data-wishlist-item-handle');

        const removeBtn = item.querySelector('[data-action="remove"]');
        if (removeBtn) {
          removeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            Wishlist.remove(handle);
          });
        }

        const addBtn = item.querySelector('[data-action="add-to-cart"]');
        if (addBtn) {
          addBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            const variantId = addBtn.getAttribute('data-variant-id');
            if (!variantId) return;
            addBtn.disabled = true;
            const original = addBtn.textContent;
            addBtn.textContent = 'Adding…';

            const cart =
              document.querySelector('cart-drawer') ||
              document.querySelector('cart-notification');

            const body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
            if (cart && typeof cart.getSectionsToRender === 'function') {
              body.sections = cart.getSectionsToRender().map((s) => s.id);
              body.sections_url = window.location.pathname;
            }

            try {
              const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
              if (!res.ok) throw new Error('add failed');
              const parsed = await res.json();

              addBtn.textContent = 'Added ✓';

              // Hand off to the theme's cart drawer if available.
              // Close the wishlist drawer FIRST and wait one frame so its
              // .active class and pointer-events are fully released before
              // the cart drawer opens — otherwise the wishlist (still
              // position:fixed across the viewport) can block clicks on the
              // freshly opened cart drawer.
              if (cart && typeof cart.renderContents === 'function' && parsed.sections) {
                this.close();
                requestAnimationFrame(() => {
                  cart.renderContents(parsed);
                  // The cart drawer's renderContents replaces #CartDrawer's
                  // innerHTML, which destroys the overlay element that the
                  // cart-drawer constructor originally bound to. Re-bind here
                  // so the overlay still closes the drawer.
                  const overlay = document.getElementById('CartDrawer-Overlay');
                  if (overlay && !overlay.dataset.wishlistRebind) {
                    overlay.addEventListener('click', () => {
                      if (typeof cart.close === 'function') cart.close();
                    });
                    overlay.dataset.wishlistRebind = '1';
                  }
                });
              }

              setTimeout(() => {
                addBtn.textContent = original;
                addBtn.disabled = false;
              }, 1200);
            } catch (err) {
              addBtn.textContent = 'Try again';
              setTimeout(() => {
                addBtn.textContent = original;
                addBtn.disabled = false;
              }, 1500);
            }
          });
        }
      });
    }

    formatMoney(cents) {
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents);
        }
      } catch (e) {}
      const dollars = (cents / 100).toFixed(2);
      const symbol =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD')
          ? '$'
          : '';
      return symbol + dollars;
    }

    escapeHtml(str) {
      return escapeHtml(str);
    }

    escapeAttr(str) {
      return escapeAttr(str);
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  // ------------------------------------------------------------------
  // Wishlist full page (rendered by sections/wishlist.liquid)
  // ------------------------------------------------------------------
  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.render();
      document.addEventListener(CHANGE_EVENT, () => this.render());
    }

    async render() {
      const grid = this.querySelector('[data-wishlist-page-grid]');
      const empty = this.querySelector('[data-wishlist-page-empty]');
      const shareWrap = this.querySelector('[data-wishlist-page-share]');
      if (!grid || !empty) return;

      // If the page was opened with ?items=..., render that shared view
      // (read-only feel: we still allow add-to-cart, but the user's own list
      // is untouched). Otherwise render the user's own wishlist.
      const isShared = Array.isArray(Wishlist.sharedHandles) && Wishlist.sharedHandles.length > 0;
      const handles = isShared ? Wishlist.sharedHandles.slice() : Wishlist.handles.slice();

      // Show/hide a banner when viewing a shared wishlist.
      // The outer <p> spans the page width (for centering), while the inner
      // <span> gets the translucent dark pill background so the box hugs
      // the text — matching .wishlist-page__empty-text / __empty-text-inner.
      let banner = this.querySelector('[data-wishlist-shared-banner]');
      if (isShared && !banner) {
        banner = document.createElement('p');
        banner.setAttribute('data-wishlist-shared-banner', '');
        banner.className = 'wishlist-page__shared-banner';
        const inner = document.createElement('span');
        inner.className = 'wishlist-page__shared-banner-inner';
        inner.textContent = "You're viewing a shared wishlist.";
        banner.appendChild(inner);
        const heading = this.querySelector('.wishlist-page__heading');
        if (heading && heading.parentNode) {
          heading.parentNode.insertBefore(banner, heading.nextSibling);
        } else {
          this.insertBefore(banner, this.firstChild);
        }
      } else if (!isShared && banner) {
        banner.remove();
      }

      if (handles.length === 0) {
        grid.hidden = true;
        grid.innerHTML = '';
        empty.hidden = false;
        if (shareWrap) shareWrap.hidden = true;
        return;
      }

      grid.hidden = false;
      empty.hidden = true;
      grid.innerHTML = '<div class="wishlist-page__loading">Loading…</div>';

      const products = await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
            if (!res.ok) return null;
            return await res.json();
          } catch (e) {
            return null;
          }
        })
      );
      const valid = products.filter(Boolean);

      if (valid.length === 0) {
        grid.hidden = true;
        grid.innerHTML = '';
        empty.hidden = false;
        if (shareWrap) shareWrap.hidden = true;
        return;
      }

      grid.innerHTML = valid.map((p) => this.renderCard(p)).join('');
      this.bindCards(grid);

      // Show share button only when viewing the user's OWN wishlist
      // (sharing someone else's shared view back doesn't make sense)
      if (shareWrap) shareWrap.hidden = isShared;
    }

    renderCard(product) {
      const variant = product.variants && product.variants[0];
      const variantId = variant ? variant.id : '';
      const available = variant ? variant.available : false;
      const image = sizedImage(product.featured_image, 600);
      const price =
        window.Shopify && typeof window.Shopify.formatMoney === 'function'
          ? window.Shopify.formatMoney(product.price)
          : '$' + (product.price / 100).toFixed(2);

      // F-001 fix: route every interpolation through the shared escape
      // helpers, matching renderItem() in the drawer class. Previously
      // only `<` was escaped on the title, and url/handle/image were
      // raw — which let a crafted product title with `"` break out of
      // the alt=/href= attribute context.
      const safeTitle = escapeHtml(product.title);
      const safeUrl = escapeAttr(product.url);
      const safeImage = escapeAttr(image);
      const safeHandle = escapeAttr(product.handle);

      // In shared-wishlist view, the displayed items belong to someone else
      // (rendered from Wishlist.sharedHandles, not Wishlist.handles), so the
      // per-card × remove button would either be a no-op (Wishlist.remove
      // bails because the handle isn't in the user's own list) or
      // semantically wrong (you can't "remove" someone else's item from
      // their share link). Hide it in shared mode.
      const isSharedView = Array.isArray(Wishlist.sharedHandles) && Wishlist.sharedHandles.length > 0;
      const removeBtnMarkup = isSharedView
        ? ''
        : '    <button type="button" class="wishlist-page__remove" data-action="remove" aria-label="Remove from wishlist">×</button>';

      return [
        '<div class="wishlist-page__card" data-handle="' + safeHandle + '">',
        '  <a href="' + safeUrl + '" class="wishlist-page__title wishlist-page__title--above">' + safeTitle + '</a>',
        '  <a href="' + safeUrl + '" class="wishlist-page__image">',
        image ? '    <img src="' + safeImage + '" alt="' + safeTitle + '" loading="lazy">' : '',
        removeBtnMarkup,
        '  </a>',
        '  <div class="wishlist-page__info">',
        '    <div class="wishlist-page__price">' + price + '</div>',
        '    <button type="button" class="wishlist-page__add button" data-action="add-to-cart" data-variant-id="' +
          variantId +
          '"' +
          (available ? '' : ' disabled') +
          '>' +
          (available ? 'Add to cart' : 'Sold out') +
          '</button>',
        '  </div>',
        '</div>',
      ].join('\n');
    }

    bindCards(grid) {
      grid.querySelectorAll('.wishlist-page__card').forEach((card) => {
        const handle = card.getAttribute('data-handle');
        const removeBtn = card.querySelector('[data-action="remove"]');
        if (removeBtn) {
          // × now lives inside the .wishlist-page__image <a>, so without
          // preventDefault + stopPropagation a click on × would bubble to
          // the parent link and navigate to the product page instead of
          // removing the item.
          removeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            Wishlist.remove(handle);
          });
        }
        const addBtn = card.querySelector('[data-action="add-to-cart"]');
        if (addBtn) {
          addBtn.addEventListener('click', async () => {
            const variantId = addBtn.getAttribute('data-variant-id');
            if (!variantId) return;
            addBtn.disabled = true;
            const original = addBtn.textContent;
            addBtn.textContent = 'Adding…';

            const cart =
              document.querySelector('cart-drawer') ||
              document.querySelector('cart-notification');
            const body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
            if (cart && typeof cart.getSectionsToRender === 'function') {
              body.sections = cart.getSectionsToRender().map((s) => s.id);
              body.sections_url = window.location.pathname;
            }

            try {
              const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
              if (!res.ok) throw new Error();
              const parsed = await res.json();
              addBtn.textContent = 'Added ✓';
              if (cart && typeof cart.renderContents === 'function' && parsed.sections) {
                cart.renderContents(parsed);
              }
              setTimeout(() => {
                addBtn.textContent = original;
                addBtn.disabled = false;
              }, 1200);
            } catch (e) {
              addBtn.textContent = 'Try again';
              setTimeout(() => {
                addBtn.textContent = original;
                addBtn.disabled = false;
              }, 1500);
            }
          });
        }
      });
    }
  }

  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', WishlistPage);
  }

  // ------------------------------------------------------------------
  // Share This Wishlist — delegated click handler (works for buttons in
  // both the wishlist page and the wishlist drawer, even after re-render).
  // ------------------------------------------------------------------
  function bindShareWishlist() {
    if (window.__ffkShareWishlistBound) return;
    window.__ffkShareWishlistBound = true;

    document.addEventListener('click', function (e) {
      const btn = e.target.closest && e.target.closest('.ffk-share-wishlist');
      if (!btn) return;

      const handles = Wishlist.handles.slice();
      const msg = btn.querySelector('.ffk-share-fit__msg');

      if (handles.length === 0) {
        // Note: the compact (drawer) share button is inside the
        // drawer footer, which is `hidden` whenever the wishlist is
        // empty (see render() above). So this branch only ever fires
        // for the non-compact page button; keeping original behavior.
        if (msg) msg.textContent = 'Wishlist is empty';
        setTimeout(() => {
          if (msg) msg.textContent = '';
        }, 2200);
        return;
      }

      const itemsParam = handles.map((h) => encodeURIComponent(h)).join(',');
      const url = window.location.origin + '/pages/wishlist?items=' + itemsParam;

      const done = function (text) {
        if (msg) msg.textContent = text;
        btn.classList.add('is-copied');
        // v2.0.5: For the drawer's compact button, the msg span is
        // pre-filled with "Copied" and the cross-fade is opacity-only
        // (see .ffk-share-wishlist--compact in assets/wishlist.css).
        // We must NOT clear textContent on the compact variant or the
        // button collapses and re-expands on the next click.
        //
        // v2.0.7: The wishlist PAGE button now uses the same
        // grid-slot + opacity cross-fade (see the
        // .wishlist-page__share-wrap block in assets/wishlist.css),
        // and its msg span is also pre-filled with "Copied" in
        // sections/wishlist.liquid. So we no longer clear textContent
        // for the page button either — both variants share the same
        // 1800ms cycle (500ms fade-in + 800ms hold + 500ms fade-out).
        setTimeout(function () {
          btn.classList.remove('is-copied');
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(function () {
            done('Copied');
          })
          .catch(function () {
            window.prompt('Copy this link:', url);
          });
      } else {
        window.prompt('Copy this link:', url);
      }
    });
  }

  // ------------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------------
  function boot() {
    Wishlist.init();
    syncAllButtons();
    bindShareWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
